import type { ReactNode } from "react";
import { packs, PACKS_URL, PACK_CTA_LABEL, packCtaAriaLabel } from "./packs";
import {
  COLOR_GUIDE_INTRO,
  FAQ_ADDITIONS,
  FIRST_TIMER_NOTE,
  MATCHA_AND_MATCHES,
  OPEN_PLAY_SUMMARY,
  PROGRAM_NOTES,
  SKILL_LEVELS,
  activePrograms,
  levelLabel,
  type DotColor,
  type Program,
} from "./skillGuide";

export type FAQ = { question: string; answer: string };
export type RelatedLink = { label: string; href: string };
export type PricingItem = { label: string; price: string; note?: string };
export type MembershipPlan = {
  name: string;
  price: string;
  cadence: string;
  /** Comparison value shown beside the price, e.g. "$450 value". */
  value?: string;
  expiry: string;
  includes: string[];
  /** `ariaLabel` is required: sibling plan buttons share one visible label. */
  cta: { label: string; ariaLabel: string; href: string; eventName: string; external?: boolean };
};

/** A program card; `note` holds details that belong to this program only. */
export type LandingProgram = Program & { note?: string };

/** A seasonal league series (shape of MATCHA_AND_MATCHES in skillGuide.ts). */
export interface LeagueSeries {
  name: string;
  color: DotColor;
  format: string;
  year: number;
  priceEarlyBird: string;
  price: string;
  priceNote: string;
  timezoneNote: string;
  recommendationNote: string;
  /** Series artwork (keep the league's own imagery). */
  image?: { src: string; srcSet: string; alt: string; width: number; height: number };
  /** YYYY-MM-DD, the finale. */
  showUntil: string;
  /** Each day is its own series per skill group; never one combined product. */
  days: {
    day: string;
    dates: string;
    time: string;
    earlyBirdEnds: string;
    /** ISO time the early-bird price ends; the line hides after it. */
    earlyBirdEndsAt: string;
    options: { level: string; url: string }[];
  }[];
}

export type LandingPageConfig = {
  slug: string; // e.g. "/pickleball-court-rental-brooklyn"
  metaTitle: string;
  metaDescription: string;
  breadcrumb: { label: string; href?: string }[]; // last is current page
  h1: string;
  subhead: string;
  heroImage: string; // path under /public
  heroImageAlt: string;
  primaryCta: { label: string; href: string; eventName: string; external?: boolean };
  secondaryCta?: { label: string; href: string; eventName: string; external?: boolean };
  intro: string[]; // paragraphs
  features: { title: string; body: string }[];
  // Long-form content blocks (each an H2 + paragraphs), rendered after the
  // feature grid. Optional — used to give thin pages real descriptive depth.
  sections?: { heading: string; body: string[] }[];
  pricing?: { heading: string; items: PricingItem[]; note?: string };
  plans?: { heading: string; items: MembershipPlan[]; note?: string };
  // Color guide slots (owner's color guide, Oct 2026). All optional; a page
  // without them renders exactly as before. Rendered after the feature grid.
  /** Compact schedule color key (seven skill colors + pink and yellow). */
  colorKey?: { heading: string; intro?: string; note?: string };
  /** Program cards: format name, copy, color chips and a link to the sessions. */
  programs?: { heading: string; intro?: string; items: LandingProgram[] };
  /** A bookable league series with one registration link per day and skill group. */
  leagueSeries?: { id: string; intro?: string[]; series: LeagueSeries };
  gallery: { src: string; alt: string }[];
  faqs: FAQ[];
  relatedLinks: RelatedLink[];
};

const podplayBooking = "https://velto.podplay.app/book";
const podplayClinics = "https://velto.podplay.app/community/events?tag=Clinics";
const podplayOpenPlay = "https://velto.podplay.app/community/events?type=Open%20Play";

const sharedLocationFaq: FAQ = {
  question: "Where is Velto Pickleball Club located?",
  answer:
    "Velto is at 160 Van Brunt Street, Brooklyn, NY 11231, in the heart of Red Hook. The 14,000 sq ft indoor facility has four full-size courts with panoramic NYC skyline views. Plenty of street parking nearby; no dedicated lot.",
};

const sharedHoursFaq: FAQ = {
  question: "What are your hours?",
  answer: "We are open daily from 6:00 AM to 12:00 AM (midnight).",
};

const sharedEquipmentFaq: FAQ = {
  question: "Do I need to bring my own equipment?",
  answer:
    "Bring your own if you'd like, but high-quality paddle and ball rentals are free of charge for everyone.",
};

const sharedPricingNote =
  "Peak hours are weekdays 5pm–10pm and weekends 8am–10pm. Off-peak everything else.";

// --- Open play + leagues: color guide copy (owner's guide, 26 Oct 2026) ---

/** Example level for copy, e.g. "High Intermediate 3.5+". */
const exampleLevel = levelLabel(SKILL_LEVELS.find((l) => l.color === "purple") ?? SKILL_LEVELS[0]);
const firstLevel = levelLabel(SKILL_LEVELS[0]);
const topLevel = levelLabel(SKILL_LEVELS[SKILL_LEVELS.length - 1]);

/** Socials-only details (flexible arrival, challenge court) stay on the socials card. */
const openPlayPrograms: LandingProgram[] = activePrograms().map((p) =>
  p.key === "socials" ? { ...p, note: PROGRAM_NOTES.socials } : p,
);

/**
 * Matcha & Matches is seasonal: the cards, their CTA and FAQ come down on
 * their own after the finale day (end of day, New York standard time).
 * Evaluated in the browser at load; the prerendered HTML follows the build date.
 */
const MATCHA_ANCHOR = "matcha-and-matches";
const matchaOpen = Date.now() <= Date.parse(`${MATCHA_AND_MATCHES.showUntil}T23:59:59-05:00`);

export const landingPages: LandingPageConfig[] = [
  // 1. Court rental
  {
    slug: "/pickleball-court-rental-brooklyn",
    metaTitle: "Pickleball Court Rental in Brooklyn, NYC | Velto from $80/hr",
    metaDescription:
      "Rent indoor pickleball courts in Red Hook, Brooklyn from $80/hr off-peak. Four full-size courts at Velto Pickleball Club. Book online, open daily 6AM–12AM.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Play", href: "/#programs" },
      { label: "Court Rental" },
    ],
    h1: "Book Indoor Pickleball Courts in Brooklyn",
    subhead:
      "Four full-size indoor courts in Red Hook. Singles, doubles, group practice — book by the hour, no membership required.",
    heroImage: "/facility/real-1.webp",
    heroImageAlt: "Indoor pickleball courts at Velto Pickleball Club in Red Hook, Brooklyn",
    primaryCta: { label: "Book a Court", href: podplayBooking, eventName: "book_court_click", external: true },
    secondaryCta: { label: "See FAQs", href: "#faqs", eventName: "court_rental_faq_scroll" },
    intro: [
      "Velto Pickleball Club offers four full-size indoor pickleball courts in Brooklyn's Red Hook neighborhood. Reserve a court for casual play, drills, or group practice — pay by the hour, no membership required.",
      "Courts are Laykold Cushion Plus surfaces inside a 14,000 sq ft facility with panoramic NYC skyline views, premium lounge areas, an automated pro shop, and smart fridges stocked with healthy meals.",
    ],
    features: [
      { title: "4 indoor courts", body: "Full-size Laykold Cushion Plus surfaces, climate controlled, open year-round." },
      { title: "Hourly rentals", body: "Book by the hour with no membership commitment. Up to 4 players per court." },
      { title: "Self check-in", body: "Unlock the door directly from your booking — no front desk wait." },
      { title: "Pro shop & rentals", body: "Free paddle and ball rentals. Paddles, grips, and accessories available 24/7." },
    ],
    sections: [
      {
        heading: "Renting a pickleball court in Brooklyn",
        body: [
          "Booking a pickleball court in Brooklyn shouldn't mean a group text, a waitlist, and a scramble for an outdoor court before the weather turns. At Velto you reserve one of four full-size indoor courts by the hour, any day from 6 AM to midnight, and the court is yours for your whole group — up to four players for doubles or two for singles. One booking covers the court, so a doubles game splits out to a low per-person cost.",
          "Every court is a Laykold Cushion Plus surface — the same cushioned acrylic used at tournament venues — inside a climate-controlled, 14,000-square-foot room in Red Hook. That means true, consistent bounces, easier footing on your knees, and play that doesn't depend on the season. Bring your own paddles or use our free rentals; either way you're on court within minutes of arriving thanks to self check-in from your booking.",
        ],
      },
      {
        heading: "Court rental or open play — which should you book?",
        body: [
          "Court rental is the right call when you already have your group and want guaranteed, uninterrupted time. Friends who want to play their own games, partners drilling a specific pattern, a parent teaching a kid, or a small crew warming up before a league night all book courts rather than buying individual open-play tickets — it's usually cheaper per person and you control the format. If you're coming solo or want to meet other players at your level, open play is the better fit; if you want a coach on the court, book a lesson.",
          "Peak hours (weekday evenings and weekend mornings) are the most popular and fill first, so if you want a specific slot, reserve a few days out. Off-peak rates are lower and there's almost always a court free — a quiet weekday afternoon in Red Hook is one of the best-value ways to play indoor pickleball in Brooklyn.",
        ],
      },
    ],
    pricing: {
      heading: "Court Rental Pricing",
      items: [
        { label: "Off-peak court rental", price: "$80 / hr", note: "Weekdays before 5pm, weekends after 10pm" },
        { label: "Peak court rental", price: "$110 / hr", note: "Weekday 5pm–10pm, weekend 8am–10pm" },
      ],
      note: sharedPricingNote,
    },
    gallery: [
      { src: "/facility/real-1.webp", alt: "Velto indoor pickleball courts" },
      { src: "/facility/real-3.webp", alt: "Court view at Velto Brooklyn" },
      { src: "/facility/real-5.webp", alt: "Velto facility interior" },
    ],
    faqs: [
      {
        question: "How do I book a court at Velto?",
        answer:
          "Click Book a Court above to open our Podplay booking system. Choose a date, time, and court, and pay online. You'll receive a confirmation email with check-in instructions.",
      },
      {
        question: "How many people can play on one court?",
        answer:
          "Each court can fit up to four players for doubles or two for singles. One booking covers the full court, so you can split the hourly rate among your group.",
      },
      {
        question: "What is the cancellation policy?",
        answer:
          "Open play bookings must be cancelled at least 12 hours in advance. Private court bookings require 24 hours' notice. Private events require one week's notice.",
      },
      sharedEquipmentFaq,
      sharedHoursFaq,
      sharedLocationFaq,
    ],
    relatedLinks: [
      { label: "Join pickleball open play in Red Hook", href: "/pickleball-open-play-brooklyn" },
      { label: "Take pickleball lessons in Brooklyn", href: "/pickleball-lessons-brooklyn" },
      { label: "Host a private pickleball event", href: "/private-pickleball-events-brooklyn" },
      { label: "View pickleball packages", href: "/pickleball-packages-brooklyn" },
      { label: "Read: Laykold vs. other pickleball court surfaces", href: "/blog/pickleball-court-surface-guide" },
    ],
  },

  // 2. Indoor pickleball courts
  {
    slug: "/indoor-pickleball-courts-brooklyn",
    metaTitle: "Indoor Pickleball Courts in Brooklyn, NYC | Velto",
    metaDescription:
      "Four indoor pickleball courts in Red Hook, Brooklyn. Climate-controlled, premium Laykold surfaces, open daily 6AM–12AM. Book a court at Velto Pickleball Club.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Play", href: "/#programs" },
      { label: "Indoor Courts" },
    ],
    h1: "Indoor Pickleball Courts in Brooklyn, NYC",
    subhead:
      "Year-round play on four full-size indoor courts in Red Hook. Premium surfaces, skyline views, easy booking — rain or shine.",
    heroImage: "/facility/real-2.webp",
    heroImageAlt: "Indoor pickleball facility at Velto in Brooklyn with skyline views",
    primaryCta: { label: "Book a Court", href: podplayBooking, eventName: "book_court_click", external: true },
    secondaryCta: { label: "Visit the FAQs", href: "#faqs", eventName: "indoor_courts_faq_link" },
    intro: [
      "Velto Pickleball Club's 14,000 sq ft indoor facility brings premium pickleball to Brooklyn year-round. Four full-size courts, climate control, and skyline views — no weather cancellations, no waiting for outdoor courts.",
      "Located in Red Hook at 160 Van Brunt Street, the club is easily reached from Carroll Gardens, Cobble Hill, Park Slope, DUMBO, and lower Manhattan.",
    ],
    features: [
      { title: "Climate controlled", body: "Play in comfort year-round — no heat, cold, wind, or rain to plan around." },
      { title: "Laykold Cushion Plus", body: "Tournament-grade indoor pickleball surface with consistent bounce and feel." },
      { title: "NYC skyline views", body: "Floor-to-ceiling windows along the playing area." },
      { title: "Premium amenities", body: "Lounge, private phone booths, automated pro shop, smart fridges with healthy meals." },
      { title: "Open 6AM–12AM", body: "Eighteen hours of bookable play every day of the week." },
      { title: "Easy NYC access", body: "Street parking on Van Brunt, B61 bus, IKEA NYC Ferry, and rideshare-friendly." },
    ],
    gallery: [
      { src: "/facility/facility-1.webp", alt: "Velto indoor pickleball courts interior" },
      { src: "/facility/facility-3.webp", alt: "Velto premium lounge area" },
      { src: "/facility/real-7.webp", alt: "Indoor court view at Velto Brooklyn" },
    ],
    faqs: [
      {
        question: "Are Velto's courts indoors?",
        answer:
          "Yes — all four courts are inside a 14,000 sq ft climate-controlled facility. Pickleball is bookable rain, snow, or shine.",
      },
      {
        question: "What kind of court surface do you use?",
        answer:
          "Velto uses Laykold Cushion Plus, a tournament-grade indoor cushioned surface chosen for consistent bounce, true ball roll, and joint comfort.",
      },
      sharedHoursFaq,
      sharedLocationFaq,
      sharedEquipmentFaq,
    ],
    relatedLinks: [
      { label: "Book a Brooklyn pickleball court", href: "/pickleball-court-rental-brooklyn" },
      { label: "Join open play sessions", href: "/pickleball-open-play-brooklyn" },
      { label: "Take beginner lessons", href: "/pickleball-lessons-brooklyn" },
      { label: "Plan a corporate event", href: "/corporate-pickleball-events-nyc" },
      { label: "Read: the complete guide to indoor pickleball in NYC", href: "/blog/indoor-pickleball-nyc-guide" },
    ],
  },

  // 3. Open play
  {
    slug: "/pickleball-open-play-brooklyn",
    metaTitle: "Pickleball Open Play in Brooklyn | Drop-In Sessions at Velto",
    metaDescription:
      "Pickleball open play in Red Hook, Brooklyn, color coded by skill level: regular, DUPR-logged, discounted, and social sessions. Each listing shows its price and length.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Play", href: "/#programs" },
      { label: "Open Play" },
    ],
    h1: "Pickleball Open Play in Red Hook, Brooklyn",
    subhead: OPEN_PLAY_SUMMARY,
    heroImage: "/facility/real-4.webp",
    heroImageAlt: "Players in open play at Velto Pickleball Club Brooklyn",
    primaryCta: { label: "Join Open Play", href: podplayOpenPlay, eventName: "open_play_signup_click", external: true },
    secondaryCta: { label: "How it works", href: "#features", eventName: "open_play_how_scroll" },
    intro: [
      "Open play at Velto is the easiest way to get on a Brooklyn court without organizing a group. Sign up individually and rotate through games with players in your session's skill group. No membership required.",
      "Formats vary by session, from regular open play and DUPR-logged games to Coffee on Us, prizes, discounted sessions, and socials. Each scheduled session lists its own price, length, and rules, so check the listing before you book.",
    ],
    features: [
      {
        title: "Color-coded levels",
        body: `${SKILL_LEVELS.length} skill colors, from ${firstLevel} to ${topLevel}, plus pink for women's programming and yellow for all-level and specialty sessions.`,
      },
      {
        title: "Priced per session",
        body: "Regular, discounted, and social sessions each list their own price and length. Browse the schedule for what's on this week.",
      },
      {
        title: "DUPR when you want it",
        body: "DUPR-Logged and Earn Your DUPR sessions submit results; other sessions don't. Any DUPR requirement is listed on the session.",
      },
      { title: "Meet the community", body: "Velto's open plays are where most members of the Brooklyn pickleball scene meet." },
    ],
    colorKey: {
      heading: "Find your color",
      intro: COLOR_GUIDE_INTRO,
      note: FIRST_TIMER_NOTE,
    },
    programs: {
      heading: "Open play formats",
      intro:
        "Color identifies the skill group or audience; the program label describes the format. Each scheduled session lists its price, length, and rules.",
      items: openPlayPrograms,
    },
    gallery: [
      { src: "/facility/real-4.webp", alt: "Open play session at Velto" },
      { src: "/facility/real-6.webp", alt: "Pickleball players in Brooklyn" },
      { src: "/facility/real-8.webp", alt: "Velto open play community" },
    ],
    faqs: [
      {
        question: "What is pickleball open play?",
        answer:
          "Open play is an organized drop-in format. You sign up individually and rotate through games with players in your session's skill group, so there's no need to bring a group. Session length, price, and rules vary by session; check the listing before you book.",
      },
      {
        question: "How much does open play cost, and how long are sessions?",
        answer:
          "It depends on the session. Regular, discounted, and social sessions each list their own price and length. Browse the schedule for current prices and session lengths.",
      },
      {
        question: "I've never played before. Is open play right for me?",
        answer: `${FIRST_TIMER_NOTE} Paddles and balls are free to rent.`,
      },
      {
        question: "How do I know which skill level to pick?",
        answer: `Use the color guide on our skill levels page (veltopb.com/levels). Each color has a written level, such as ${exampleLevel}, and a description of how players at that level play. A session's level and its DUPR requirement are separate, so check the listing for any minimum rating. If you're unsure, start a level lower; you can always move up.`,
      },
      ...FAQ_ADDITIONS,
      sharedEquipmentFaq,
      sharedHoursFaq,
      sharedLocationFaq,
    ],
    relatedLinks: [
      { label: "See the color guide", href: "/levels" },
      { label: "Book a private court instead", href: "/pickleball-court-rental-brooklyn" },
      { label: "Take a lesson before joining open play", href: "/pickleball-lessons-brooklyn" },
      { label: "Join a pickleball league", href: "/pickleball-leagues-brooklyn" },
      { label: "Read: what to expect at your first open play", href: "/blog/pickleball-open-play-what-to-expect" },
    ],
  },

  // 4. Lessons
  {
    slug: "/pickleball-lessons-brooklyn",
    metaTitle: "Pickleball Lessons in Brooklyn | Private & Group | Velto",
    metaDescription:
      "Pickleball lessons in Red Hook, Brooklyn. Beginner clinics from $60, private coaching, and group sessions with certified instructors at Velto Pickleball Club.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Learn", href: "/#programs" },
      { label: "Lessons" },
    ],
    h1: "Pickleball Lessons in Brooklyn for Beginners and Experienced Players",
    subhead:
      "Learn from certified instructors in a 14,000 sq ft indoor facility. Beginner clinics, private coaching, and group lessons for every level.",
    heroImage: "/facility/real-3.webp",
    heroImageAlt: "Pickleball lesson at Velto Pickleball Club Brooklyn",
    primaryCta: { label: "Book a Lesson", href: podplayClinics, eventName: "lesson_booking_click", external: true },
    secondaryCta: { label: "See clinic schedule", href: podplayClinics, eventName: "lesson_schedule_click", external: true },
    intro: [
      "Velto runs pickleball lessons and clinics in Brooklyn for players from total beginner up through tournament prep. Sessions are taught by certified instructors on Velto's indoor Laykold courts in Red Hook.",
      "Whether you've never picked up a paddle or you want to sharpen your third-shot drop, there's a format that fits.",
    ],
    features: [
      { title: "Beginner clinics", body: "Learn the rules, scoring, basic strokes, and dink fundamentals in a low-pressure group setting." },
      { title: "Private lessons", body: "One-on-one or two-on-one coaching tailored to your DUPR level and goals." },
      { title: "Group clinics", body: "Skill-leveled group classes covering serves, returns, transitions, and strategy." },
      { title: "Certified instructors", body: "Velto's coaches are PPR / IPTPA-certified and active competitive players." },
    ],
    sections: [
      {
        heading: "Pickleball lessons in Brooklyn for every level",
        body: [
          "Velto's pickleball lessons in Brooklyn are built around one idea: you learn fastest when you're actually playing, not standing in a line. Beginner clinics get a paddle in your hand in the first few minutes and teach the rules, scoring, and the core strokes — serve, return, forehand, backhand, and the all-important dink — in the flow of real points. By the end of a 90-minute session most first-timers can rally, keep score, and hold their own in a beginner game.",
          "More experienced players use private and semi-private lessons to fix specific things: a third-shot drop that keeps floating, footwork at the kitchen line, or serve consistency under pressure. Because coaching is one-on-one or two-on-one, the session is shaped entirely around your goals and your DUPR level instead of a fixed curriculum.",
        ],
      },
      {
        heading: "Which lesson format is right for you?",
        body: [
          "If you have never played, start with a beginner group clinic — it's the most affordable way in and you'll meet other people at exactly your stage. If you can already keep score and want to climb from, say, a 3.0 to a 3.5, skill-leveled group clinics drill the specific patterns that separate those levels. If you're preparing for a tournament or trying to break a plateau, book a private lesson and bring the one or two things you most want to fix.",
          "Not sure where you fall? Our skill-levels guide breaks down the 1.0-through-advanced ratings we use to sort clinics and open play, so you can self-select the right room. When you're between two levels, book the lower one first — a session that moves a little slower gives you more reps and more coaching per point.",
        ],
      },
      {
        heading: "How to book a lesson",
        body: [
          "All clinics and lessons are booked online through our Podplay system — pick a session, pay, and you'll get a confirmation with self check-in details, so there's no front desk to wait at. Paddles and balls are included free of charge, so you need nothing but athletic clothes, clean court shoes, and water for your first session. For private coaching or to be matched with a specific coach, book through the clinics page or email support@veltopb.com and we'll pair you with the right instructor.",
        ],
      },
    ],
    pricing: {
      heading: "Lessons & Clinics Pricing",
      items: [
        { label: "Group clinic", price: "$60 per player", note: "Skill-leveled, ~90 minutes" },
        // TODO-JAWAD[lessons-private-price]: "From $20+" for a private (1-on-1 / 2-on-1) lesson looks implausibly low next to the $60 group clinic and the beginners guide's "$25 intro class" — confirm the real private-lesson starting price. — Option A: keep "From $20+" | Option B: correct the figure | ANSWER: ____
        { label: "Private lesson", price: "From $20+", note: "Pricing varies by coach and format — see schedule" },
      ],
    },
    gallery: [
      { src: "/facility/real-2.webp", alt: "Pickleball clinic at Velto" },
      { src: "/facility/real-5.webp", alt: "Velto Brooklyn lesson session" },
      { src: "/facility/facility-4.webp", alt: "Velto coaching environment" },
    ],
    faqs: [
      {
        question: "I've never played pickleball. Where should I start?",
        answer:
          "Start with a Beginner Clinic — you'll learn rules, scoring, and the basic strokes in about 90 minutes. From there, jump into a beginner open play to practice what you learned.",
      },
      {
        question: "Do you offer private lessons?",
        answer:
          "Yes. Private lessons can be booked through the Podplay events page filtered to Clinics, or by emailing support@veltopb.com to be matched with a coach.",
      },
      {
        question: "Are paddles provided for lessons?",
        answer:
          "Yes — high-quality paddle and ball rentals are free for all lesson and clinic participants.",
      },
      sharedHoursFaq,
      sharedLocationFaq,
    ],
    relatedLinks: [
      { label: "See all skill levels", href: "/levels" },
      { label: "Join a beginner open play", href: "/pickleball-open-play-brooklyn" },
      { label: "Book a court to practice", href: "/pickleball-court-rental-brooklyn" },
      { label: "Join a pickleball league", href: "/pickleball-leagues-brooklyn" },
      { label: "Read: a beginner's guide to starting at Velto", href: "/blog/pickleball-beginners-guide" },
    ],
  },

  // 5. Corporate events
  {
    slug: "/corporate-pickleball-events-nyc",
    metaTitle: "Corporate Pickleball Events & Team Building NYC | Velto",
    metaDescription:
      "Host corporate pickleball events and team building in NYC at Velto's 14,000 sq ft Brooklyn facility. Four courts, healthy catering, fully buyout-able. Request a quote.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Events", href: "/events" },
      { label: "Corporate Events" },
    ],
    h1: "Corporate Pickleball Events and Team Building in NYC",
    subhead:
      "Buy out four indoor pickleball courts in Brooklyn for your next company offsite, team-building event, or client experience.",
    heroImage: "/facility/facility-5.webp",
    heroImageAlt: "Corporate pickleball event setup at Velto Brooklyn",
    primaryCta: { label: "Request Event Info", href: "/events", eventName: "corporate_event_inquiry_click" },
    secondaryCta: { label: "See private events", href: "/private-pickleball-events-brooklyn", eventName: "corporate_to_private_link" },
    intro: [
      "Velto Pickleball Club is one of the only fully indoor, fully bookable pickleball venues in NYC suited to corporate groups. Buy out the whole facility, mix court time with lounge networking, and add healthy catering and coaching.",
      "We've hosted team-building events, client experiences, executive offsites, and holiday parties for companies based in Manhattan and Brooklyn.",
    ],
    features: [
      { title: "Up to ~80 guests", body: "Four full-size courts plus lounge seating support groups from 10 to ~80 people comfortably." },
      { title: "Full or partial buyout", body: "Reserve the entire venue or a subset of courts depending on group size." },
      { title: "Coached programming", body: "Add certified instructors for mixer-style clinics, mini-tournaments, or skills challenges." },
      { title: "Healthy catering", body: "Smart-fridge gourmet options on-site; outside catering welcome. (Non-alcoholic facility.)" },
      { title: "Branded experience", body: "Signage, prizes, and event programming customized to your company." },
      { title: "Easy NYC logistics", body: "Red Hook location 15 minutes from lower Manhattan — easy for whole-team transport." },
    ],
    gallery: [
      { src: "/facility/facility-5.webp", alt: "Velto corporate event courts setup" },
      { src: "/facility/facility-6.webp", alt: "Velto lounge area for events" },
      { src: "/facility/facility-7.webp", alt: "Velto group event view" },
    ],
    faqs: [
      {
        question: "How many people can attend a corporate event?",
        answer:
          "Comfortably ~80 guests across four courts and the lounge area, depending on format. Smaller groups (10–30) work great with a partial court buyout.",
      },
      {
        question: "Can you provide instructors and run programming?",
        answer:
          "Yes. Certified coaches can run beginner clinics, mixer-style rotations, or a friendly mini-tournament with prizes — great for groups where players are at mixed levels.",
      },
      {
        question: "Do you serve alcohol?",
        answer:
          "No — Velto is a non-alcoholic facility. We offer healthy food and drink options and provide an alcohol-free environment to bring people together.",
      },
      {
        question: "How far in advance should I book?",
        answer:
          "Reach out as early as possible, especially for full buyouts and end-of-year dates. Most corporate events are confirmed 4–8 weeks in advance.",
      },
      sharedLocationFaq,
    ],
    relatedLinks: [
      { label: "Private events at Velto", href: "/private-pickleball-events-brooklyn" },
      { label: "Birthday party packages", href: "/pickleball-birthday-party-brooklyn" },
      { label: "Submit an event inquiry", href: "/events" },
      { label: "Contact the events team", href: "/contact" },
    ],
  },

  // 6. Private events
  {
    slug: "/private-pickleball-events-brooklyn",
    metaTitle: "Private Pickleball Events in Brooklyn | Velto",
    metaDescription:
      "Host private pickleball events in Red Hook, Brooklyn. Birthdays, bachelor/ettes, friend groups, social mixers, and mini-tournaments at Velto. Request a quote.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Events", href: "/events" },
      { label: "Private Events" },
    ],
    h1: "Private Pickleball Events in Brooklyn",
    subhead:
      "Bring your group to Velto for a private pickleball event in Red Hook. Birthdays, bachelor/ettes, friend mixers, social leagues, mini-tournaments.",
    heroImage: "/facility/facility-6.webp",
    heroImageAlt: "Private pickleball event at Velto Brooklyn",
    primaryCta: { label: "Request Event Info", href: "/events", eventName: "private_event_inquiry_click" },
    secondaryCta: { label: "Corporate events", href: "/corporate-pickleball-events-nyc", eventName: "private_to_corporate_link" },
    intro: [
      "Velto's four indoor courts and lounge space are available for private groups. Whether it's a birthday, a bachelor or bachelorette, a friend mixer, or a mini-tournament, our team will help you map out court time, programming, and catering.",
      "All events take place in a non-alcoholic, wellness-forward setting — built for energy, connection, and play.",
    ],
    features: [
      { title: "Birthdays & celebrations", body: "Court time + lounge + optional coaching turns a birthday into something memorable." },
      { title: "Bachelor / bachelorette parties", body: "An active, alcohol-free alternative to the usual itinerary." },
      { title: "Friend & social mixers", body: "Rotation-style play keeps everyone in games and meeting new people." },
      { title: "Mini-tournaments", body: "Bracketed play with prizes — we'll handle the format." },
      { title: "Flexible group sizes", body: "From 8 friends on one court to a full buyout for ~80." },
      { title: "Healthy food on-site", body: "Smart-fridge gourmet meals, snacks, and non-alcoholic drinks." },
    ],
    gallery: [
      { src: "/facility/facility-6.webp", alt: "Velto private event setup" },
      { src: "/facility/facility-8.webp", alt: "Velto lounge for private events" },
      { src: "/facility/real-7.webp", alt: "Velto courts ready for private play" },
    ],
    faqs: [
      {
        question: "What's the minimum group size for a private event?",
        answer:
          "There's no hard minimum — a single court works well for 4–8 people. For larger groups, we'll suggest multiple courts or a full buyout.",
      },
      {
        question: "Can you accommodate mixed skill levels?",
        answer:
          "Yes. Coaches and structured formats (round-robin, social mixer, mini-tournament) make mixed-level events work for both beginners and stronger players.",
      },
      {
        question: "Is outside food allowed?",
        answer:
          "Yes, outside catering is welcome. Velto is a non-alcoholic venue — outside alcohol is not permitted.",
      },
      {
        question: "How do I get a quote?",
        answer:
          "Submit the form on the Events page with your date, group size, and event type. The events team responds within 24 hours.",
      },
      sharedLocationFaq,
    ],
    relatedLinks: [
      { label: "Corporate pickleball events", href: "/corporate-pickleball-events-nyc" },
      { label: "Birthday party packages", href: "/pickleball-birthday-party-brooklyn" },
      { label: "Submit an event inquiry", href: "/events" },
      { label: "Visit Velto in Red Hook", href: "/contact" },
    ],
  },

  // 7. Packages
  {
    slug: "/pickleball-packages-brooklyn",
    metaTitle: "Pickleball Packages in Brooklyn | Velto Pickleball Club",
    metaDescription:
      "Prepaid pickleball packages at Velto in Red Hook, Brooklyn. Open play passes, clinics passes, guest passes, and court hours. See what each package includes.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Packages" },
    ],
    h1: "Pickleball Packages in Brooklyn",
    subhead:
      "Prepaid packages of open play passes, clinics passes, and court hours at Brooklyn's premier indoor pickleball club.",
    heroImage: "/facility/real-5.webp",
    heroImageAlt: "Velto players on court in Red Hook, Brooklyn",
    primaryCta: { label: "Find Out More", href: "/contact", eventName: "membership_inquiry_click" },
    secondaryCta: { label: "Book a court first", href: podplayBooking, eventName: "membership_to_booking_click", external: true },
    intro: [
      "Velto offers prepaid packages — buy your passes or court hours up front, then use them whenever you play. Every package is good for 6 months after purchase.",
      "Packages are optional. Velto is open to everyone, and you can always book a court or join open play as a one-off.",
      "There are four packages, all listed below.",
    ],
    features: [
      { title: "All Access open play passes", body: "Three packages include All Access open play passes — 21 in the All Access 21 Open Play Pack, ten in the All Access 10+2 Open Play Pack, and five in the Velto Beginners Pack." },
      { title: "Guest passes included", body: "The All Access 10+2 Open Play Pack includes two All Access open play guest passes, so you can bring a friend." },
      { title: "Clinics passes", body: "The Velto Beginners Pack includes four clinics passes, which can be used for a clinic series, alongside its open play passes." },
      { title: "A Velto house paddle", body: "The Velto Beginners Pack includes one Velto house paddle." },
      { title: "Private court hours", body: "The Buy 5 Court Hours Get 1 Free pack is six private court hours, at peak or off-peak times, plus 100 Replay Credits." },
      { title: "Community first", body: "Be part of the Red Hook pickleball community — meet players at every level." },
    ],
    plans: {
      heading: "Packages",
      items: packs.map((pack) => ({
        name: pack.name,
        price: pack.price,
        cadence: pack.cadence,
        value: pack.value,
        expiry: pack.expiry,
        includes: pack.includes,
        cta: {
          label: PACK_CTA_LABEL,
          ariaLabel: packCtaAriaLabel(pack),
          href: PACKS_URL,
          eventName: `pack_${pack.id.replace(/-/g, "_")}_click`,
          external: true,
        },
      })),
    },
    gallery: [
      { src: "/facility/real-5.webp", alt: "Velto players on court" },
      { src: "/facility/real-8.webp", alt: "Velto community" },
      { src: "/facility/facility-3.webp", alt: "Velto players in the lounge" },
    ],
    faqs: [
      {
        question: "Do I need a membership to play at Velto?",
        answer:
          "No. You can book a court or join open play any time without buying anything up front. Packages are prepaid passes and are entirely optional.",
      },
      {
        question: "How do I buy a package?",
        answer:
          "Buy any package on the Velto packages page, or email support@veltopb.com and the team will walk you through what's included.",
      },
      {
        question: "What's in each package?",
        // Built from packs.ts so the prices here can't drift from the cards.
        answer: `${packs
          .map((pack) => `${pack.name} (${pack.price}${pack.value ? `, a ${pack.value}` : ""}): ${pack.includes.join(", ")}.`)
          .join(" ")} Every package expires 6 months after purchase.`,
      },
      sharedHoursFaq,
      sharedLocationFaq,
    ],
    relatedLinks: [
      { label: "Book a court", href: "/pickleball-court-rental-brooklyn" },
      { label: "Join open play", href: "/pickleball-open-play-brooklyn" },
      { label: "Join a league", href: "/pickleball-leagues-brooklyn" },
      { label: "Take a lesson", href: "/pickleball-lessons-brooklyn" },
    ],
  },

  // 8. Leagues
  {
    slug: "/pickleball-leagues-brooklyn",
    metaTitle: "Pickleball Leagues in Brooklyn & NYC | Velto Ladders",
    metaDescription: matchaOpen
      ? "Pickleball leagues in Red Hook, Brooklyn: register online for Matcha & Matches, our coach-guided women's league, or join a ladder with weekly standings at Velto."
      : "Pickleball leagues and ladders in Red Hook, Brooklyn. Beginner, intermediate, and advanced divisions with weekly standings at Velto Pickleball Club.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Play", href: "/#programs" },
      { label: "Leagues" },
    ],
    h1: "Pickleball Leagues in Brooklyn",
    subhead: matchaOpen
      ? "Register online for Matcha & Matches, our coach-guided women's league, or compete in recurring ladders with beginner, intermediate, and advanced divisions and weekly standings."
      : "Compete in recurring pickleball leagues and ladders at Velto. Beginner, intermediate, and advanced divisions with weekly standings.",
    heroImage: "/facility/real-6.webp",
    heroImageAlt: "Pickleball league play at Velto Brooklyn",
    // Matcha & Matches is bookable online, so it gets the primary CTA; contact
    // stays for the ladders, which are still arranged with the team.
    primaryCta: matchaOpen
      ? { label: "Join Matcha & Matches", href: `#${MATCHA_ANCHOR}`, eventName: "leagues_register_scroll" }
      : { label: "Contact for league info", href: "/contact", eventName: "leagues_contact_click" },
    secondaryCta: matchaOpen
      ? { label: "Contact for ladder info", href: "/contact", eventName: "leagues_contact_click" }
      : undefined,
    intro: [
      "Velto runs ongoing pickleball leagues and ladders for Brooklyn players who want regular competitive play. Ladders are divided by level so games are evenly matched and standings stay meaningful.",
      "Ladder players sign up by skill division, play weekly matches at Velto's indoor courts, and earn ladder placement based on results.",
    ],
    features: [
      { title: "Three divisions", body: "Beginner, Intermediate, and Advanced ladders — sign up where you fit best." },
      { title: "Weekly standings", body: "Live ladder standings tracked across the season and shared with active league members." },
      { title: "Indoor, year-round", body: "League play happens on Velto's indoor courts — no weather delays." },
      { title: "Mix of formats", body: "Combination of singles, doubles, and round-robin formats depending on the season." },
    ],
    leagueSeries: matchaOpen
      ? {
          id: MATCHA_ANCHOR,
          intro: [
            `${MATCHA_AND_MATCHES.name} is Velto's coach-guided women's league. Each skill group has its own series on each day, so choose one day and one skill group, then register online.`,
            `Included: ${MATCHA_AND_MATCHES.included.join(" ")}`,
          ],
          series: MATCHA_AND_MATCHES,
        }
      : undefined,
    gallery: [
      { src: "/facility/real-6.webp", alt: "Velto league players in action" },
      { src: "/facility/real-4.webp", alt: "Pickleball ladder play at Velto" },
      { src: "/facility/real-1.webp", alt: "Velto indoor courts for league play" },
    ],
    faqs: [
      matchaOpen
        ? {
            question: "How do I join a Velto league?",
            answer: `Register for ${MATCHA_AND_MATCHES.name} online: choose your day and skill group in the ${MATCHA_AND_MATCHES.name} section on this page. For ladders, contact the Velto team and we'll share current divisions, standings, and the next available start dates.`,
          }
        : {
            question: "How do I join a Velto league?",
            answer:
              "Contact the Velto team to register for the division that matches your level. We'll share current divisions, standings, and the next available start dates.",
          },
      ...(matchaOpen
        ? [
            {
              question: `Do I need a DUPR rating for ${MATCHA_AND_MATCHES.name}?`,
              answer: `No. Choose ${MATCHA_AND_MATCHES.days[0].options.map((o) => o.level).join(" or ")} by how you play: ${MATCHA_AND_MATCHES.recommendationNote}`,
            },
          ]
        : []),
      {
        question: "What level should I sign up for?",
        answer:
          "If you're new to competitive play, start in Beginner. The color guide on our skill levels page describes each level if you're unsure — you can always move divisions as you improve.",
      },
      {
        question: "Do I need a partner to join?",
        answer:
          "Not necessarily — many league formats include singles ladders and rotating doubles. Contact the team for specifics on the current season.",
      },
      sharedHoursFaq,
      sharedLocationFaq,
    ],
    relatedLinks: [
      { label: "Practice with open play", href: "/pickleball-open-play-brooklyn" },
      { label: "See the color guide", href: "/levels" },
      { label: "Book a court for league prep", href: "/pickleball-court-rental-brooklyn" },
      { label: "Take a lesson to level up", href: "/pickleball-lessons-brooklyn" },
    ],
  },

  // 9. Birthday parties
  {
    slug: "/pickleball-birthday-party-brooklyn",
    metaTitle: "Pickleball Birthday Parties in Brooklyn | Velto",
    metaDescription:
      "Host a pickleball birthday party in Red Hook, Brooklyn. Indoor courts, coaching, healthy catering options, and a non-alcoholic, wellness-forward facility.",
    breadcrumb: [
      { label: "Home", href: "/" },
      { label: "Events", href: "/events" },
      { label: "Birthday Parties" },
    ],
    h1: "Pickleball Birthday Parties in Brooklyn",
    subhead:
      "Throw a memorable birthday at Velto. Indoor courts, optional coaching, healthy catering, and a non-alcoholic, wellness-forward setting.",
    heroImage: "/facility/facility-8.webp",
    heroImageAlt: "Pickleball birthday party setup at Velto Brooklyn",
    primaryCta: { label: "Request Party Info", href: "/events", eventName: "birthday_inquiry_click" },
    secondaryCta: { label: "Other private events", href: "/private-pickleball-events-brooklyn", eventName: "birthday_to_private_link" },
    intro: [
      "Pickleball is the easiest birthday format to actually pull off — everyone gets up, gets moving, and has a great time, regardless of skill level. Velto's indoor Red Hook facility makes it weather-proof and reservable year-round.",
      "Pair court time with a coach-led mini-clinic, a round-robin format, or just open play with your group. We'll help you choose what fits.",
    ],
    features: [
      { title: "Adult birthdays", body: "Most-requested format. One or more courts plus lounge time — great for groups of 8–40." },
      { title: "Family-friendly", body: "Kids and teens are welcome with adult supervision. Ask the team about family formats." },
      { title: "Optional coaching", body: "Add a coach for a 30–60 minute warm-up so beginners can play with confidence." },
      { title: "Healthy catering", body: "Smart-fridge gourmet options on-site; outside catering welcome. No alcohol." },
      { title: "Easy planning", body: "One inquiry form, one events lead — we'll handle the rest." },
    ],
    gallery: [
      { src: "/facility/facility-8.webp", alt: "Birthday party setup at Velto" },
      { src: "/facility/facility-7.webp", alt: "Velto lounge for birthday events" },
      { src: "/facility/real-3.webp", alt: "Indoor courts ready for party play" },
    ],
    faqs: [
      {
        question: "How many people can I invite to a birthday party at Velto?",
        answer:
          "From 8 friends on one court to ~80 across a full buyout. Most birthday parties land in the 12–40 range and use 2–4 courts.",
      },
      {
        question: "Can you set up a mini-tournament for the party?",
        answer:
          "Yes — a bracketed mini-tournament with prizes is one of the most popular birthday formats. We'll provide the structure and a coach if needed.",
      },
      {
        question: "Do you allow alcohol?",
        answer:
          "No — Velto is a non-alcoholic facility. We offer healthy food and drink options and an energetic alcohol-free environment.",
      },
      {
        question: "How do I book a birthday party?",
        answer:
          "Use the form on the Events page. Include your preferred date, group size, and any catering or coaching requests. The events team replies within 24 hours.",
      },
      sharedLocationFaq,
    ],
    relatedLinks: [
      { label: "Private events at Velto", href: "/private-pickleball-events-brooklyn" },
      { label: "Corporate events", href: "/corporate-pickleball-events-nyc" },
      { label: "Submit an event inquiry", href: "/events" },
      { label: "Contact the events team", href: "/contact" },
    ],
  },
];

export const landingPagesBySlug: Record<string, LandingPageConfig> = Object.fromEntries(
  landingPages.map((p) => [p.slug, p]),
);

// Suppress unused-type-import warning for ReactNode
export type _ReactNode = ReactNode;
